import { Box, Button, Grid, Tab, Theme, Typography, useTheme } from '@mui/material'
import React, { useMemo, useRef } from 'react'
import BreadCrum from '../../../lib/BreadCrum'
import { useHistory } from 'react-router';
import { useSelector } from 'react-redux';
import * as yup from 'yup';
import { RootStateType } from '../../../redux-store/reducers';
import { TabContext, TabList } from '@mui/lab';
import { Formik, FormikProps, FormikValues, useFormikContext } from 'formik';
import { CustomSlider } from '../../../lib/formik/CustomSlider';
import { isRMLikeRole, toINR } from '../../../utils/utilityFunctions';
import CustomePieChart from '../../dashboard/chart/CustomePieChart';

// ---- Types ----
type Mode = 'SIP' | 'Lumpsum';

type CalculatorInput = {
    mode: Mode;
    amount: number;
    expectedReturnPA: number;
    years: number;
};

type MoneyOut = { value: number; currency: string };
type CalculatorOutput = { invested: MoneyOut; returns: MoneyOut; total: MoneyOut };

// ---- Form initial values ----
const InitalValues: FormikValues = {
    activeTab: 'SIP',
    sip: { sipInv: 25000, expectedReturn: 12, timePeriod: 10 },
    lumpsum: { lumpsumInv: 25000, expectedReturn: 12, timePeriod: 10 }
};


// ---- Finance helpers ----
const toMonthlyRate = (annualPct: number) =>
    Math.pow(1 + annualPct / 100, 1 / 12) - 1; // effective monthly [web:31]

const fvSipOrdinary = (pmtMonthly: number, rAnnualPct: number, years: number) => {
    const i = toMonthlyRate(rAnnualPct);
    const n = years * 12;
    if (i === 0) return pmtMonthly * n; // 0% return — no growth, just sum of payments
    return pmtMonthly * ((Math.pow(1 + i, n) - 1) / i) * (1 + i); // [web:31][web:12]
};

const fvLumpsum = (pv: number, rAnnualPct: number, years: number) =>
    pv * Math.pow(1 + rAnnualPct / 100, years); // FV = PV*(1+r)^n [web:9][web:12]

function calculateAll(
    input: CalculatorInput,
    format: (x: { value: number; locale: string; currencyCode: string }) => string,
    locale: string,
    currencyCode: string
): CalculatorOutput {
    if (input.mode === 'SIP') {
        const n = input.years * 12;
        const invested = input.amount * n;
        const fv = fvSipOrdinary(input.amount, input.expectedReturnPA, input.years);
        const returns = Math.max(0, Math.round(fv - invested));
        const total = Math.round(invested + returns);
        return {
            invested: { value: invested, currency: format({ value: invested, locale, currencyCode }) },
            returns: { value: returns, currency: format({ value: returns, locale, currencyCode }) },
            total: { value: total, currency: format({ value: total, locale, currencyCode }) }
        };
    }
    const invested = input.amount;
    const fv = fvLumpsum(invested, input.expectedReturnPA, input.years);
    const returns = Math.max(0, Math.round(fv - invested));
    const total = Math.round(fv);
    return {
        invested: { value: invested, currency: format({ value: invested, locale, currencyCode }) },
        returns: { value: returns, currency: format({ value: returns, locale, currencyCode }) },
        total: { value: total, currency: format({ value: total, locale, currencyCode }) }
    };
}


// ---- Calculator UI ----
const Calculator = ({ activeTab }: { activeTab: string }) => {
    const theme = useTheme();
    const { values, errors }: FormikProps<FormikValues> = useFormikContext();
    const { locale, currencyCode } = useSelector((state: RootStateType) => state.currencyInfo);

    const mode: Mode = activeTab === 'SIP' ? 'SIP' : 'Lumpsum';
    const amount = mode === 'SIP' ? values?.sip?.sipInv ?? 0 : values?.lumpsum?.lumpsumInv ?? 0;
    const expectedReturnPA = mode === 'SIP' ? values?.sip?.expectedReturn ?? 0 : values?.lumpsum?.expectedReturn ?? 0;
    const years = mode === 'SIP' ? values?.sip?.timePeriod ?? 0 : values?.lumpsum?.timePeriod ?? 0;

    // If any field invalid, short-circuit calculations and chart
    const hasErrors = useMemo(() => {
        const e: any = errors;
        return Boolean(
            e?.sip?.sipInv || e?.sip?.expectedReturn || e?.sip?.timePeriod ||
            e?.lumpsum?.lumpsumInv || e?.lumpsum?.expectedReturn || e?.lumpsum?.timePeriod
        );
    }, [errors]);

    const calc = useMemo(() => {
        if (hasErrors) {
            const zero = { value: 0, currency: toINR({ value: 0, locale, currencyCode }) };
            return { invested: zero, returns: zero, total: zero };
        }
        return calculateAll({ mode, amount: Number(amount), expectedReturnPA: Number(expectedReturnPA), years: Number(years) }, toINR, locale, currencyCode);
    }, [hasErrors, mode, amount, expectedReturnPA, years, locale, currencyCode]);

    const estimatedReturnColor = theme.palette.primary[500] ?? "#1BBAC8";
    const investedAmountColor = theme.palette.primary[800] ?? "#114f8c";
    const colorArray = [estimatedReturnColor, investedAmountColor];

    const pieData = useMemo(
        () => ([
            ["Est. Return", calc.returns.value],
            ["Invested Amount", calc.invested.value],
        ]),
        [calc.returns.value, calc.invested.value]
    );

    const totalValue = useMemo(
        () => ({ label: "Total Value", value: calc.total.value }),
        [calc.total.value]
    );

    const chartKey = useMemo(
        () => `${calc.returns.value}-${calc.invested.value}-${calc.total.value}`,
        [calc.returns.value, calc.invested.value, calc.total.value]
    );

    return (
        <Grid container columnSpacing={{ xs: 0, md: 3 }} rowSpacing={{ xs: 4, md: 6 }} alignItems="flex-start">
            {/* Left: sliders */}
            <Grid item xs={12} md={7}>
                <Grid container direction="column" rowSpacing={5}>
                    <Grid item>
                        <CustomSlider
                            name={activeTab === "SIP" ? "sip.sipInv" : "lumpsum.lumpsumInv"}
                            label={activeTab === "SIP" ? "Monthly Investment" : "Total Investment"}
                            max={activeTab === "SIP" ? 1000000 : 10000000}
                            // min={1}
                            inputMode="numeric"
                            startCharacter='₹'
                            sx={{
                                maxWidth: 130,
                            }}
                        />
                    </Grid>
                    <Grid item>
                        <CustomSlider
                            name={activeTab === "SIP" ? "sip.expectedReturn" : "lumpsum.expectedReturn"}
                            label="Expected return rate (p.a)"
                            max={30}
                            // min={1}
                            endCharacter="%"
                            inputMode='decimal'
                        />
                    </Grid>
                    <Grid item>
                        <CustomSlider
                            name={activeTab === "SIP" ? "sip.timePeriod" : "lumpsum.timePeriod"}
                            label="Time Period"
                            max={40}
                            // min={1}
                            endCharacter="Yr"
                            inputMode="numeric"
                        />
                    </Grid>

                    <Box sx={(theme: Theme) => ({ border: `0.1px dotted ${theme.palette.grey[400]}B2`, mt: 3 })} />
                    <Box sx={{ mt: 3 }} />
                    <Grid container spacing={2}>
                        <Grid item xs>
                            <Typography variant='body_500' sx={{ color: "text.secondary" }}>Invested Amount</Typography>
                        </Grid>
                        <Grid item>
                            <Typography variant='body_600'>{calc.invested.currency}</Typography>
                        </Grid>
                    </Grid>
                    <Grid container mt={2}>
                        <Grid item xs>
                            <Typography variant='body_500' sx={{ color: "text.secondary" }}>Est Return</Typography>
                        </Grid>
                        <Grid item>
                            <Typography variant='body_600'>{calc.returns.currency}</Typography>
                        </Grid>
                    </Grid>
                    <Grid container mt={2}>
                        <Grid item xs>
                            <Typography variant='body_500' sx={{ color: "text.secondary" }}>Total Value</Typography>
                        </Grid>
                        <Grid item>
                            <Typography variant='body_600'>{calc.total.currency}</Typography>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>

            {/* Right: chart and CTA */}
            <Grid container item xs={12} md={5} direction="column" rowSpacing={4} alignItems="center" justifyContent="center" width={"100%"} maxWidth={"100%"}>
                <Grid item container justifyContent="center" columnSpacing={6} sx={{ width: '100%', flexWrap: 'nowrap' }}>
                    <Grid item sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2 }}>
                        <Box sx={{ width: 12, height: 12, borderRadius: "50%", bgcolor: "primary.500" }} />
                        <Typography variant='body_500'>Est Return</Typography>
                    </Grid>
                    <Grid item sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2 }}>
                        <Box sx={{ width: 12, height: 12, borderRadius: "50%", bgcolor: "primary.800" }} />
                        <Typography variant='body_500'>Invested Amount</Typography>
                    </Grid>
                </Grid>

                {/* Donut chart: render only when no errors */}
                <Grid item sx={{ width: '100%' }}>
                    {!hasErrors && (
                        <CustomePieChart
                            key={chartKey}
                            data={pieData}
                            isLoading={false}
                            labelData={[]}
                            totalValue={totalValue}
                            showSlider={false}
                            align='center'
                            colorArr={colorArray}
                            labelOrderEnabled
                        />
                    )}
                </Grid>

                <Grid item>
                    <Button
                        variant='contained'
                        sx={{ px: 5, py: 1, bgcolor: "primary.500", borderRadius: 2, border: "none", cursor: "pointer" }}
                        onClick={() => history.back()}
                    >
                        <Typography variant='body_500'>Invest Now</Typography>
                    </Button>
                </Grid>
            </Grid>
        </Grid>
    );
};

// ---- Page component ----
export default function SIPCalculator(): JSX.Element {
    const history = useHistory();
    const { pathname: pathName } = history.location
    const { role = '' } = useSelector((store: RootStateType) => store.auth);
    const theme = useTheme();
    const initialRouter = pathName.split('/').filter((item) => item !== '')
    const tabList = ['SIP', 'Lumpsum'];
    const formikRef = useRef<FormikProps<any>>(null);
    const { familyMemberId = "" } = useSelector((store: RootStateType) => store.id);

    const rmRouterPath = [
        { name: 'Dashboard', path: '/dashboard' },
        ...(!initialRouter.includes('product') ? [{ name: initialRouter[1], path: `/${initialRouter[0]}/${initialRouter[1]}` }] : []),
        {
            name: initialRouter[0] === 'product' ? "Mutual Funds" : 'Detailed Portfolio Holdings',
            path: initialRouter[0] === 'product'
                ? `/${initialRouter[0]}/${initialRouter[1]}`
                : `/${initialRouter[0]}/${initialRouter[1]}/${initialRouter[2]}`
        },
        {
            name: 'Explore Mutual Funds',
            path: initialRouter[0] === 'product'
                ? `/${initialRouter[0]}/${initialRouter[1]}/${initialRouter[2]}`
                : `/${initialRouter[0]}/${initialRouter[1]}/${initialRouter[2]}/${initialRouter[3]}`
        },
    ]

    const investorRouterPath = [...(familyMemberId
        ?
        [{
            name: 'View Family',
            path: `/investor-family/family-profile/${familyMemberId}`,
        },
        {
            name: `${initialRouter[1]}`,
            path: `/${initialRouter[0]}/${initialRouter[1]}`,
        },
        {
            name: 'Detailed Portfolio Holdings',
            path: `/${initialRouter[0]}/${initialRouter[1]}/${initialRouter[2]}`
        },
        { name: 'Explore Mutual Funds', path: `/${initialRouter[0]}/${initialRouter[1]}/${initialRouter[2]}/${initialRouter[3]}` },
        ]
        :
        [
            { name: 'Dashboard', path: '/investor-dashboard' },
            { name: 'Detailed Portfolio Holdings', path: `/${initialRouter[0]}/${initialRouter[1]}` },
            { name: 'Explore Mutual Funds', path: `/${initialRouter[0]}/${initialRouter[1]}/${initialRouter[2]}` },
        ])
    ];

    const handleSubmit = () => { console.log('submit') }

    const handleChange = (event: React.SyntheticEvent, newValue: string) => {
        formikRef.current?.setFieldValue('activeTab', newValue);
    }

    const validationSchema = yup.object().shape({
        activeTab: yup.string().required('Active tab is required'),
        sip: yup.object().shape({
            sipInv: yup.string()
                .test('sipInv', (value, context) => {
                    const fieldValue = value === null ? '' : value;
                    const { createError, path, options } = context;
                    const { context: optionsContext = {} } = options
                    if (`${optionsContext?.activeTab ?? ''}`.toLowerCase() !== 'sip') return true;
                    if (!fieldValue) {
                        return createError({
                            message: 'SIP Investment amount is required',
                            path,
                        })
                    }
                    if (fieldValue?.includes('-') || fieldValue?.includes('+')) {
                        return createError({
                            message: 'SIP Investment amount can\'t have invalid characters',
                            path,
                        })
                    }
                    if (Number.isNaN(Number(fieldValue))) {
                        return createError({
                            message: 'SIP Investment amount should be a valid number',
                            path,
                        })
                    }
                    return true;
                }),
            expectedReturn: yup
                .string()
                .test('expectedReturn', (value, context) => {
                    const fieldValue = value === null ? '' : value;
                    const { createError, path, options } = context;
                    const { context: optionsContext = {} } = options
                    if (`${optionsContext?.activeTab ?? ''}`.toLowerCase() !== 'sip') return true;
                    if (!fieldValue) {
                        return createError({
                            message: 'Expected return is required',
                            path,
                        })
                    }
                    if (fieldValue?.includes('-') || fieldValue?.includes('+')) {
                        return createError({
                            message: 'Expected return can\'t have invalid characters',
                            path,
                        })
                    }
                    if (Number.isNaN(Number(fieldValue))) {
                        return createError({
                            message: 'Expected return should be a valid number',
                            path,
                        })
                    }
                    return true;
                }),
            timePeriod: yup
                .string()
                .test('timePeriod', (value, context) => {
                    const fieldValue = value === null ? '' : value;
                    const { createError, path, options } = context;
                    const { context: optionsContext = {} } = options
                    if (`${optionsContext?.activeTab ?? ''}`.toLowerCase() !== 'sip') return true;
                    if (!fieldValue) {
                        return createError({
                            message: 'Time period is required',
                            path,
                        })
                    }
                    if (fieldValue?.includes('-') || fieldValue?.includes('+')) {
                        return createError({
                            message: 'Time period can\'t have invalid characters',
                            path,
                        })
                    }
                    if (Number.isNaN(Number(fieldValue))) {
                        return createError({
                            message: 'Time period should be a valid number',
                            path,
                        })
                    }
                    return true;
                }),
        }),
        lumpsum: yup.object().shape({
            lumpsumInv: yup.string()
                .test('lumpsumInv', (value, context) => {
                    const fieldValue = value === null ? '' : value;
                    const { createError, path, options } = context;
                    const { context: optionsContext = {} } = options
                    if (`${optionsContext?.activeTab ?? ''}`.toLowerCase() === 'sip') return true;
                    if (!fieldValue) {
                        return createError({
                            message: 'Lumpsum Investment Amount is required',
                            path,
                        })
                    }
                    if (fieldValue?.includes('-') || fieldValue?.includes('+')) {
                        return createError({
                            message: 'Lumpsum Investment Amount can\'t have invalid characters',
                            path,
                        })
                    }
                    if (Number.isNaN(Number(fieldValue))) {
                        return createError({
                            message: 'Lumpsum Investment Amount should be a valid number',
                            path,
                        })
                    }
                    return true;
                }),
            expectedReturn: yup
                .string()
                .test('expectedReturn', (value, context) => {
                    const fieldValue = value === null ? '' : value;
                    const { createError, path, options } = context;
                    const { context: optionsContext = {} } = options
                    if (`${optionsContext?.activeTab ?? ''}`.toLowerCase() === 'sip') return true;
                    if (!fieldValue) {
                        return createError({
                            message: 'Expected return is required',
                            path,
                        })
                    }
                    if (fieldValue?.includes('-') || fieldValue?.includes('+')) {
                        return createError({
                            message: 'Expected return can\'t have invalid characters',
                            path,
                        })
                    }
                    if (Number.isNaN(Number(fieldValue))) {
                        return createError({
                            message: 'Expected return should be a valid number',
                            path,
                        })
                    }
                    return true;
                }),
            timePeriod: yup
                .string()
                .test('timePeriod', (value, context) => {
                    const fieldValue = value === null ? '' : value;
                    const { createError, path, options } = context;
                    const { context: optionsContext = {} } = options
                    if (`${optionsContext?.activeTab ?? ''}`.toLowerCase() === 'sip') return true;
                    if (!fieldValue) {
                        return createError({
                            message: 'Time period is required',
                            path,
                        })
                    }
                    if (fieldValue?.includes('-') || fieldValue?.includes('+')) {
                        return createError({
                            message: 'Time period can\'t have invalid characters',
                            path,
                        })
                    }
                    if (Number.isNaN(Number(fieldValue))) {
                        return createError({
                            message: 'Time period should be a valid number',
                            path,
                        })
                    }
                    return true;
                }),
        }),
    });

    return (
        <Box>
            <BreadCrum array={isRMLikeRole(role) ? rmRouterPath : investorRouterPath} />
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", marginTop: '10px' }}>
                <Typography variant="h1" sx={(theme: Theme) => ({ color: theme.palette.text.primary, mb: { xs: "10px", sm: "0px" } })}>
                    SIP Calculators
                </Typography>
            </Box>
            <Box sx={(theme: Theme) => ({
                marginTop: "25px",
                borderRadius: "25px",
                backgroundColor: theme.palette.common.white,
                boxShadow: `0px 4px 10px 0px ${theme.palette.common.black}0D`,
                padding: "18px"
            })}>
                <Formik
                    initialValues={InitalValues}
                    enableReinitialize={true}
                    onSubmit={handleSubmit}
                    innerRef={formikRef}
                    // validate={validate}
                    validationSchema={validationSchema}
                    validateOnChange={true}
                // validateOnBlur
                >
                    {({ values }) => {
                        return (
                            <TabContext value={values.activeTab}>
                                <Box sx={{
                                    borderBottom: `1px solid ${theme.palette.text.secondary}99`,
                                    display: 'flex',
                                    flexDirection: 'row',
                                    paddingBottom: { lg: '0px', md: '0px', sm: '0.1px', xs: '0.1px' },
                                }}>
                                    <TabList
                                        sx={{ '& .MuiTabs-indicator': { display: 'none' }, minHeight: 28, height: 28 }}
                                        {...values}
                                        onChange={handleChange}
                                    >
                                        {tabList.map((item, index) => {
                                            return (
                                                <Tab
                                                    label={
                                                        <Typography variant='body_500' sx={(theme: Theme) => ({
                                                            color: values.activeTab === item ? theme.palette.common.white : theme.palette.text.secondary,
                                                            textTransform: 'capitalize'
                                                        })}>
                                                            {item}
                                                        </Typography>
                                                    }
                                                    sx={{
                                                        minHeight: 28, height: 28,
                                                        px: { xs: 5, sm: 1.5 }, py: { xs: 0, sm: 1.5 }, mr: 2,
                                                        borderRadius: '7px 7px 0px 0px',
                                                        background: values.activeTab === item ? theme.palette.primary[500] : theme.palette.grey[50]
                                                    }}
                                                    key={index}
                                                    value={item}
                                                />
                                            )
                                        })}
                                    </TabList>
                                </Box>
                                <Box sx={{ mt: 4 }}>
                                    <Calculator activeTab={values.activeTab} />
                                </Box>
                            </TabContext>
                        )
                    }}
                </Formik>
            </Box >
        </Box >
    )
}
